import { Button } from '@/components/shadcn/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/shadcn/tabs'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/shadcn/command'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { RestSchemas, useRestClient } from '@/contexts/QueryProvider'
import clsx from 'clsx'
import { AlertCircle, ChevronLeft, ChevronRight, History, Loader2, Plus, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DMP_CHAT_APPLICATION_KEY } from '../dmpChatContract'
import {
  clearDmpChatLastActive,
  readDmpChatLastActive,
  selectDmpChatLastActive,
  writeDmpChatLastActive,
} from '../dmpChatLastActive'
import { refetchDmpChatHistoryUntilVisible } from '../dmpChatHistoryRefresh'
import { DmpChatSessionFiles } from './DmpChatSessionFiles'
import { useDmpChatAgent, type DmpChatAvailableAgent } from './DmpChatAgentContext'
import { useDmpChatRuntime } from './DmpChatRuntimeProvider'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/shadcn/badge'
import { AgentIcon } from '@/const'
import { IconV2FileOutline } from '@icons/IconV2FileOutline'
import { IconV2CommentsMessagesOutline } from '@icons/IconV2CommentsMessagesOutline'

type ChatHistoryMeta = RestSchemas['Messages.ChatHistory.SavedSessionMeta']
type DmpChatSidebarTab = 'history' | 'files'

interface DmpChatHistoryProps {
  organisationId: number | undefined
  projectId: number
  className?: string
}

export function DmpChatHistory({ organisationId, projectId, className }: DmpChatHistoryProps) {
  const { deleteChatHistoryApiDeleteSession, getChatHistoryApiGetHistory, getChatHistoryApiGetSession } =
    useRestClient()
  const { agents } = useDmpChatAgent()
  const {
    cancelResume,
    chatHistoryId,
    isHydrating,
    isTurnActive,
    newChat,
    prepareResume,
    resumeHistory,
    sidebarTabRequest,
  } = useDmpChatRuntime()
  const [isOpen, setIsOpen] = useState(true)
  const [selectedTab, setSelectedTab] = useState<DmpChatSidebarTab>('history')
  const [selectedHistoryId, setSelectedHistoryId] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null)
  const [deletedHistoryIds, setDeletedHistoryIds] = useState<ReadonlySet<string>>(() => new Set())
  const [historyStatus, setHistoryStatus] = useState<string | null>(null)
  const [resumeError, setResumeError] = useState<string | null>(null)
  const wasTurnActiveRef = useRef(false)
  const hasRestoredProjectRef = useRef(false)

  const historyPath = useMemo(
    () => ({
      organisationId: organisationId ?? 0,
      projectId,
      appKey: DMP_CHAT_APPLICATION_KEY,
    }),
    [organisationId, projectId]
  )
  const historyQuery = getChatHistoryApiGetHistory.useQuery({ path: historyPath }, { enabled: organisationId != null })
  const { refetch: refetchHistory } = historyQuery
  const { mutateAsync: deleteHistorySession, isPending: isDeleting } = deleteChatHistoryApiDeleteSession.useMutation()
  const sessionQuery = getChatHistoryApiGetSession.useQuery(
    {
      path: {
        ...historyPath,
        chatHistoryId: selectedHistoryId ?? '',
      },
    },
    {
      enabled: false,
    }
  )
  const { refetch: refetchSession } = sessionQuery

  const historyItems = useMemo(
    () =>
      (historyQuery.data ?? [])
        .filter((session) => {
          const sessionId = session.chatHistoryId
          return (
            typeof sessionId === 'string' &&
            sessionId.trim().length > 0 &&
            !deletedHistoryIds.has(sessionId) &&
            (session as ChatHistoryMeta & { deleted?: unknown }).deleted !== true
          )
        })
        .sort((first, second) => (second.lastUpdatedAt ?? 0) - (first.lastUpdatedAt ?? 0)),
    [deletedHistoryIds, historyQuery.data]
  )

  const isHistoryActionDisabled = isTurnActive || isHydrating || isDeleting
  const isLoadingSession = selectedHistoryId !== null && (sessionQuery.isLoading || sessionQuery.isFetching)
  const isNewChatOpen = !historyItems.some((session) => session.chatHistoryId === chatHistoryId)
  const handleResumeFailure = useCallback(
    (message: string) => {
      cancelResume()
      clearDmpChatLastActive(projectId)
      newChat()
      setResumeError(message)
      setHistoryStatus(null)
      setSelectedHistoryId(null)
    },
    [cancelResume, newChat, projectId]
  )

  useEffect(() => {
    if (wasTurnActiveRef.current && !isTurnActive) {
      void refetchDmpChatHistoryUntilVisible(chatHistoryId, refetchHistory)
    }
    wasTurnActiveRef.current = isTurnActive
  }, [chatHistoryId, isTurnActive, refetchHistory])

  useEffect(() => {
    if (!selectedHistoryId) {
      return
    }

    let isActive = true
    const loadSelectedHistory = async () => {
      try {
        const result = await refetchSession()
        if (!isActive) {
          return
        }

        if (result.isError || !result.data || result.data.chatHistoryId !== selectedHistoryId) {
          handleResumeFailure('This chat history could not be loaded. A new chat has been started.')
          return
        }

        if (!resumeHistory(result.data)) {
          handleResumeFailure('This chat history is no longer available. A new chat has been started.')
          return
        }

        writeDmpChatLastActive(projectId, result.data.chatHistoryId!)
        setSelectedHistoryId(null)
      } catch {
        if (isActive) {
          handleResumeFailure('This chat history could not be loaded. A new chat has been started.')
        }
      }
    }

    void loadSelectedHistory()
    return () => {
      isActive = false
    }
  }, [handleResumeFailure, projectId, refetchSession, resumeHistory, selectedHistoryId])

  const handleResume = useCallback(
    (historyId: string) => {
      if (isHistoryActionDisabled) {
        return
      }

      if (!prepareResume()) {
        return
      }

      setResumeError(null)
      setHistoryStatus(null)
      setSelectedHistoryId(historyId)
    },
    [isHistoryActionDisabled, prepareResume]
  )

  const handleNewChat = () => {
    if (isHistoryActionDisabled || !newChat()) {
      return
    }

    clearDmpChatLastActive(projectId)
    setResumeError(null)
    setHistoryStatus('New chat started.')
    setSelectedHistoryId(null)
  }

  useEffect(() => {
    if (!sidebarTabRequest) {
      return
    }

    setIsOpen(true)
    setSelectedTab(sidebarTabRequest.tab)
  }, [sidebarTabRequest])

  const handleDelete = useCallback(async () => {
    if (!deleteTarget || organisationId == null || isHistoryActionDisabled) {
      return
    }

    try {
      await deleteHistorySession({
        path: {
          ...historyPath,
          chatHistoryId: deleteTarget.id,
        },
      })

      if (readDmpChatLastActive(projectId) === deleteTarget.id) {
        clearDmpChatLastActive(projectId)
      }

      const deletedOpenThread = deleteTarget.id === chatHistoryId
      if (deletedOpenThread) {
        newChat()
      }

      setDeletedHistoryIds((previous) => new Set(previous).add(deleteTarget.id))
      setDeleteTarget(null)
      setResumeError(null)
      setHistoryStatus(
        deletedOpenThread ? `Deleted ${deleteTarget.title}. New chat started.` : `Deleted ${deleteTarget.title}.`
      )
      void refetchHistory()
    } catch {
      setHistoryStatus(`Could not delete ${deleteTarget.title}. Try again.`)
    }
  }, [
    chatHistoryId,
    deleteHistorySession,
    deleteTarget,
    historyPath,
    isHistoryActionDisabled,
    newChat,
    organisationId,
    projectId,
    refetchHistory,
  ])

  useEffect(() => {
    if (organisationId == null || historyQuery.isLoading || historyQuery.isError || hasRestoredProjectRef.current) {
      return
    }

    hasRestoredProjectRef.current = true
    const storedChatHistoryId = readDmpChatLastActive(projectId)
    const availableChatHistoryIds = historyItems.flatMap((session) =>
      session.chatHistoryId ? [session.chatHistoryId] : []
    )
    const historyIdToRestore = selectDmpChatLastActive(storedChatHistoryId, availableChatHistoryIds)
    if (historyIdToRestore) {
      handleResume(historyIdToRestore)
      return
    }

    if (storedChatHistoryId) {
      clearDmpChatLastActive(projectId)
    }
    newChat()
  }, [handleResume, historyItems, historyQuery.isError, historyQuery.isLoading, newChat, organisationId, projectId])

  useEffect(() => {
    if (
      !historyQuery.isSuccess ||
      isTurnActive ||
      isHydrating ||
      !historyItems.some((session) => session.chatHistoryId === chatHistoryId)
    ) {
      return
    }

    writeDmpChatLastActive(projectId, chatHistoryId)
  }, [chatHistoryId, historyItems, historyQuery.isSuccess, isHydrating, isTurnActive, projectId])

  return (
    <aside
      aria-label="DMP Chat history"
      className={cn(
        'flex min-h-0 shrink-0 flex-col overflow-hidden rounded-lg border bg-background transition-[width]',
        isOpen ? 'w-full md:w-80' : 'w-12',
        className
      )}
    >
      <div
        className={clsx('flex shrink-0 items-center border-b p-2', isOpen ? 'justify-between gap-2' : 'justify-center')}
      >
        {isOpen && (
          <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
            <h2 className="flex min-w-0 items-center gap-2 px-1 text-sm font-semibold">Chat history</h2>
            <Button
              aria-current={isNewChatOpen ? 'page' : undefined}
              className={cn(isNewChatOpen && 'border-primary bg-primary/5')}
              disabled={isHistoryActionDisabled}
              onClick={handleNewChat}
              size="xs"
              type="button"
              variant="outline"
            >
              <Plus className="size-3.5" />
              New chat
            </Button>
          </div>
        )}
        <button
          aria-controls="dmp-chat-sidebar-content"
          aria-expanded={isOpen}
          aria-label={isOpen ? 'Collapse DMP Chat history' : 'Expand DMP Chat history'}
          className="inline-flex size-8 shrink-0 items-center justify-center rounded-md hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => setIsOpen((open) => !open)}
          type="button"
        >
          {isOpen ? <ChevronLeft className="size-4" /> : <ChevronRight className="size-4" />}
        </button>
      </div>

      {isOpen && (
        <div className="flex min-h-0 flex-1 flex-col">
          <div
            aria-atomic="true"
            aria-live="polite"
            className="min-h-10 border-b px-3 py-2 text-xs text-muted-foreground"
            role="status"
          >
            {isLoadingSession && (
              <span className="flex items-center gap-2">
                <Loader2 className="size-3.5 animate-spin" />
                Loading Thread…
              </span>
            )}
            {!isLoadingSession && isHydrating && 'Resuming Thread…'}
            {!isLoadingSession && !isHydrating && resumeError}
            {!isLoadingSession && !isHydrating && !resumeError && historyStatus}
            {!isLoadingSession &&
              !isHydrating &&
              !resumeError &&
              !historyStatus &&
              isTurnActive &&
              'History actions are disabled during a Turn.'}
          </div>

          <Tabs
            className="flex min-h-0 flex-1 flex-col"
            id="dmp-chat-sidebar-content"
            onValueChange={(value) => {
              if (value === 'history' || value === 'files') {
                setSelectedTab(value)
              }
            }}
            value={selectedTab}
          >
            <TabsList aria-label="DMP Chat sidebar" className="mx-2 mt-2 w-auto shrink-0">
              <TabsTrigger
                className="flex-1 data-[state=active]:bg-background data-[state=active]:font-bold"
                value="history"
              >
                <IconV2CommentsMessagesOutline className="mr-2 size-5" />
                Chats
              </TabsTrigger>
              <TabsTrigger
                className="flex-1 data-[state=active]:bg-background data-[state=active]:font-bold"
                value="files"
              >
                <IconV2FileOutline className="mr-2 size-5" />
                Files
              </TabsTrigger>
            </TabsList>

            <TabsContent className="min-h-0 flex-1 overflow-y-auto p-2 pt-0" value="history">
              {historyQuery.isLoading && (
                <div className="flex items-center gap-2 px-2 py-4 text-sm text-muted-foreground" role="status">
                  <Loader2 className="size-4 animate-spin" />
                  Loading chat history…
                </div>
              )}

              {!historyQuery.isLoading && historyQuery.isError && (
                <div className="flex flex-col items-start gap-2 px-2 py-4 text-sm text-muted-foreground" role="alert">
                  <span className="flex items-center gap-2">
                    <AlertCircle className="size-4" />
                    Chat history is unavailable. You can still use the current Thread.
                  </span>
                  <Button onClick={() => void historyQuery.refetch()} size="xs" type="button" variant="outline">
                    Try again
                  </Button>
                </div>
              )}

              {!historyQuery.isLoading && !historyQuery.isError && historyItems.length === 0 && (
                <div className="flex flex-col items-center gap-2 px-2 py-8 text-center text-sm text-muted-foreground">
                  <History className="size-5" />
                  No saved DMP Chat history for this project.
                </div>
              )}

              {!historyQuery.isLoading && !historyQuery.isError && historyItems.length > 0 && (
                <Command className="h-full rounded-none border-0 bg-transparent">
                  <CommandInput
                    aria-label="Search DMP Chat history"
                    className="h-full py-2"
                    placeholder="Search chats..."
                    wrapperClassName="h-9 rounded-sm border border-input bg-background"
                  />
                  <CommandList className="max-h-none flex-1 overflow-y-auto p-2">
                    <CommandEmpty>No chats found.</CommandEmpty>
                    <CommandGroup className="p-0">
                      {historyItems.map((session) => (
                        <CommandItem
                          key={session.chatHistoryId}
                          className="mb-1 items-start rounded-md p-0 text-foreground data-[selected=true]:bg-muted data-[selected=true]:text-foreground"
                          onSelect={() => handleResume(session.chatHistoryId!)}
                          value={`${session.title ?? ''} ${session.preview ?? ''} ${session.agentKey ?? ''}`}
                        >
                          <HistoryListItem
                            disabled={isHistoryActionDisabled}
                            isOpen={session.chatHistoryId === chatHistoryId}
                            onDelete={() =>
                              setDeleteTarget({
                                id: session.chatHistoryId!,
                                title: session.title?.trim() || 'Untitled conversation',
                              })
                            }
                            onSelect={() => handleResume(session.chatHistoryId!)}
                            agents={agents}
                            session={session}
                          />
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              )}
            </TabsContent>

            <TabsContent className="min-h-0 flex-1 overflow-y-auto p-2 pt-0" value="files">
              <DmpChatSessionFiles
                key={chatHistoryId}
                chatHistoryId={chatHistoryId}
                isActive={selectedTab === 'files'}
                organisationId={organisationId}
                projectId={projectId}
              />
            </TabsContent>
          </Tabs>
        </div>
      )}
      <ConfirmDialog
        confirmLabel="Delete chat"
        confirmVariant="destructive"
        description={
          deleteTarget
            ? `Delete "${deleteTarget.title}"? This chat will be removed from your DMP Chat history.`
            : undefined
        }
        isOpen={deleteTarget !== null}
        isSubmitting={isDeleting}
        closeOnConfirm={false}
        onConfirm={() => void handleDelete()}
        onOpenChange={(open) => {
          if (!open && !isDeleting) {
            setDeleteTarget(null)
          }
        }}
        title="Delete chat history?"
      />
    </aside>
  )
}

function HistoryListItem({
  agents,
  disabled,
  isOpen,
  onDelete,
  onSelect,
  session,
}: {
  agents: readonly DmpChatAvailableAgent[] | undefined
  disabled: boolean
  isOpen: boolean
  onDelete: () => void
  onSelect: () => void
  session: ChatHistoryMeta
}) {
  const title = session.title?.trim() || 'Untitled conversation'
  const preview = session.preview?.trim() || 'No preview available.'
  const sessionAgentKey = session.agentKey?.trim()
  const agent = agents?.find((entry) => entry.config?.key === sessionAgentKey)
  const agentLabel = agent?.name ?? agent?.config?.name ?? sessionAgentKey ?? 'Project Agent'
  const recency = formatRecency(session.lastUpdatedAt)

  return (
    <div
      className={clsx(
        'flex w-full items-stretch rounded-md border transition-colors hover:bg-muted',
        isOpen ? 'border-primary bg-primary/5' : 'border-transparent'
      )}
    >
      <button
        aria-current={isOpen ? 'page' : undefined}
        aria-label={`${title}. ${preview}. Agent: ${agentLabel}. ${recency}. Resume Thread.`}
        className="flex min-w-0 flex-1 flex-col items-start gap-1 rounded-l-md px-3 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        disabled={disabled}
        onClick={onSelect}
        type="button"
      >
        <span className="line-clamp-1 w-full text-sm font-medium">{title}</span>
        <span className="line-clamp-2 w-full text-xs text-muted-foreground">{preview}</span>
        <span className="flex w-full items-center justify-between gap-2 text-[11px] text-muted-foreground">
          <Badge variant="secondary" className="h-5">
            <AgentIcon />
            {agentLabel}
          </Badge>
          <time dateTime={toDateTime(session.lastUpdatedAt)}>{recency}</time>
        </span>
      </button>
      <Button
        aria-label={`Delete ${title}`}
        className="mt-2 self-start rounded-md"
        disabled={disabled}
        onClick={(event) => {
          event.stopPropagation()
          onDelete()
        }}
        onMouseDown={(event) => {
          event.preventDefault()
          event.stopPropagation()
        }}
        size="icon-xs"
        title={`Delete ${title}`}
        type="button"
        variant="ghost"
      >
        <Trash2 className="size-4" />
      </Button>
    </div>
  )
}

function toDateTime(timestamp: number | undefined): string | undefined {
  if (typeof timestamp !== 'number' || !Number.isFinite(timestamp)) {
    return undefined
  }

  return new Date(timestamp).toISOString()
}

function formatRecency(timestamp: number | undefined): string {
  if (typeof timestamp !== 'number' || !Number.isFinite(timestamp)) {
    return 'Unknown time'
  }

  const elapsedSeconds = Math.round((Date.now() - timestamp) / 1000)
  if (elapsedSeconds < 60) {
    return 'Just now'
  }
  if (elapsedSeconds < 3_600) {
    return `${Math.floor(elapsedSeconds / 60)}m ago`
  }
  if (elapsedSeconds < 86_400) {
    return `${Math.floor(elapsedSeconds / 3_600)}h ago`
  }
  if (elapsedSeconds < 604_800) {
    return `${Math.floor(elapsedSeconds / 86_400)}d ago`
  }

  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(timestamp))
}
